import type { GetServerSidePropsContext } from 'next'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'

import { StaticDataProvider } from '@mc/static-data-react'
import { Separator } from '@mc/design-system'
import { BogoBanner } from '@mc/bogo'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { TRACK_PROPS } from '@mc/track-event'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'

import {
  getArticle,
  getArticleCategories,
  getDefaultProduct,
  getGuestPassByReferralToken,
  getV2Categories,
} from 'libs/server/api'
import { articleCategoryPath } from 'libs/paths'
import { getPageData } from 'libs/server/page-data'
import {
  ARTICLE_TYPE,
  CATEGORIES_TYPE,
  GUEST_PASS_TYPE,
  PRODUCT_PLAN_TYPE,
} from 'types'
import { CategoryNavCarousel } from 'components/CategoryNavCarousel'
import GuestPassBanner from 'components/GuestPassBanner'

import { logger } from '@mc/next-logger'
import { redirect } from 'libs/utils'

import Meta from './components/Meta'
import Intro from './components/Intro'
import QuizBanner from './components/Quiz'
import StickyFooter from '../components/StickyFooter'
import InstructorCarousel from './components/InstructorsCarousel'
import Details from './components/Details'
import {
  trackArticleViewed,
  trackCategoryCarouselClick,
} from './components/events'
import { getRedirectPathForArticle } from './libs/articleRedirects'

const BelowTheFold = dynamic(() => import('./components/BelowTheFold'), {
  ssr: false,
})

type Ret<T extends (...args: never[]) => unknown> = Awaited<ReturnType<T>>

type ArticlePageProps = {
  annualPass: Ret<typeof getDefaultProduct>
  article: Ret<typeof getArticle>
  articleCategories: Ret<typeof getArticleCategories>
  guestPass: Ret<typeof getGuestPassByReferralToken>
  hasCompletedQuiz: boolean
  shouldAutoOpenQuiz: boolean
}

const Article = ({
  annualPass,
  article,
  articleCategories,
  guestPass,
  hasCompletedQuiz,
  shouldAutoOpenQuiz,
}: ArticlePageProps) => {
  useEffect(() => {
    trackArticleViewed(article)
  }, [article])

  return (
    <StaticDataProvider
      value={{ annualPass, article, articleCategories, guestPass }}
      providerKey={article.slug}
    >
      <TrackingParamsProvider
        value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-articles' }}
      >
        <Meta />
        <BogoBanner />
        <GuestPassBanner />
        {!hasCompletedQuiz && (
          <QuizBanner shouldAutoOpenQuiz={shouldAutoOpenQuiz} />
        )}
        <PromoExplainerModal pageSource={PAGE_SOURCES.ARTICLE_PAGE} />

        <Separator className='mc-opacity--muted mc-m-0' />
        <div className='container'>
          <div className='mc-my-2'>
            <CategoryNavCarousel
              categories={articleCategories}
              pathCategory={articleCategoryPath}
              onNavClick={trackCategoryCarouselClick}
            />
          </div>
        </div>
        <div className='mc-theme-light mc-bg-light'>
          <Intro />
          <InstructorCarousel className='col-lg-8 offset-lg-1' />
          <Details />
          <BelowTheFold />
        </div>
        <StickyFooter />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Article.propTypes = {
  article: ARTICLE_TYPE,
  articleCategories: CATEGORIES_TYPE,
  annualPass: PRODUCT_PLAN_TYPE,
  guestPass: GUEST_PASS_TYPE,
  hasCompletedQuiz: PropTypes.bool,
  instructors: PropTypes.array,
  shouldAutoOpenQuiz: PropTypes.bool,
}

Article.pageSettings = {
  analyticsLabel: 'Article Page',
  showPromo: true,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug },
    req: {
      cookies: {
        referral_token: referralToken,
        'quiz-completed': quizCompleted,
      },
      headers: { host, referer },
    },
  } = ctx

  if (typeof slug !== 'string') {
    logger.error('Invalid slug', { slug })
    throw new Error('Invalid slug')
  }
  const redirectLocation = getRedirectPathForArticle(slug)
  if (redirectLocation) {
    return redirect(ctx, redirectLocation, { queryExclude: ['slug'] })
  }

  const hasCompletedQuiz = quizCompleted === 'true'
  const hasMCReferrer = referer && referer.indexOf(host || '') !== -1
  const shouldAutoOpenQuiz = !!hasMCReferrer && !hasCompletedQuiz

  return getPageData({
    criticalPromises: {
      article: getArticle(ctx, slug),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      articleCategories: getArticleCategories(ctx),
      categories: getV2Categories(ctx),
      guestPass: getGuestPassByReferralToken(ctx, { referralToken }),
    },
    propsMapper: ({ ...restProps }) => ({
      hasCompletedQuiz: hasCompletedQuiz || false,
      shouldAutoOpenQuiz,
      ...restProps,
    }),
  })
}

export default Article
